import styles from './CardParcelModalWindow.module.scss';
import cn from 'classnames';
import ToggleButtons from "@components/ToggleButtons";
import ParcelModalTimeline from "@components/ParcelModalTimeline";
import InfoIcon from '@assets/icons/info-grey-18.svg';
import { Button, ButtonType } from '@components/Button';
import { ErrorResponse } from '@api/Responses/ErrorResponse';
import { formatSumWithCurrency, formatNumber } from '@utils/formatValues';
import { getParcel, getParcelPhoto, TParcelResponse } from '@api/apiClient/returns';
import { getParcelTrackEvents } from '@api/apiClient';
import { TAwaitingParcel } from '@api/apiClient/returns';
import { ICardParcelModalWindowProps, TPhotoData, TPhotosData } from '../../types';
import { Loader } from '@components/Loader';
import { Text, TextColor, TextSize, TextWeight } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import { Tooltip } from '@components/new/Tooltip';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';

const CardParcelModalWindow: React.FC<ICardParcelModalWindowProps> = (props: ICardParcelModalWindowProps): React.ReactElement => {
  const {
    callFrom,
    handlerSetIsOpenDisposalProcessingWindow,
    handlerSetIsOpenParcelWindow,
    handlerSetIsOpenResaleProcessingWindow,
    handlerSetIsOpenReturnProcessingWindow,
    handlerSetIsOpenDeliverToLocalProcessingWindow,
    handlerSetProcessingCalledFrom,
    parcel
  } = props;

  const [checkingPhotos, setCheckingPhotos] = useState<string[]>([]);
  const [isLoadingParcelPhoto, setIsLoadingParcelPhoto] = useState<boolean>(true);
  const [photo, setPhoto] = useState<string | undefined>(undefined);
  const [photoVisible, setPhotoVisible] = useState<boolean>(false);
  const [returnPhotos, setReturnPhotos] = useState<string[]>([]);

  const { account } = useProfile();
  const { t } = useTranslation();

  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  const {
    data: dataGetParcel,
    isFetching: isFetchingGetParcel
  } = useQuery<TParcelResponse, ErrorResponse>({
    queryKey: ['parcel', parcel.id],
    queryFn: () => getParcel(parcel.id),
    retry: 1
  });

  const isUNI = Boolean(dataGetParcel?.item.linkedParcelId);

  const {
    data: trackEventsData,
    isFetching: isFetchingTrackEvents,
  } = useQuery({
    queryKey: ['getParcelTrackEvents', dataGetParcel?.item?.linkedParcelId],
    queryFn: () => getParcelTrackEvents(dataGetParcel?.item?.linkedParcelId ?? ""),
    retry: 1,
    enabled: isUNI,
  });

  const { mutateAsync: mutateAsyncGetParcelPhoto } = useMutation({
    mutationFn: getParcelPhoto
  });

  const setPhotos = (checkingPhotos: TPhotosData, returnPhotos: TPhotosData): void => {
    (async () => {
      try {
        const checkingImages = await Promise.all(
            checkingPhotos.map(async (item: TPhotoData) => {
              const blob = await mutateAsyncGetParcelPhoto(item.storageKey);
              return URL.createObjectURL(blob.data);
            })
        );
        const returnImages = await Promise.all(
            returnPhotos.map(async (item: TPhotoData) => {
              const blob = await mutateAsyncGetParcelPhoto(item.storageKey);
              return URL.createObjectURL(blob.data);
            })
        );
        setCheckingPhotos(checkingImages);
        setReturnPhotos(returnImages);
        setIsLoadingParcelPhoto(false);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  useEffect(() => {
    if (dataGetParcel?.item) {
      setPhotos(dataGetParcel?.item.checkingPhotos, dataGetParcel?.item.returnPhotos);
    }
  }, [dataGetParcel]);

  const [statusView, setStatusView] = useState<string>("return");

  useEffect(() => {
    setPhotoVisible(!!photo);
  }, [photo]);

  if (isFetchingGetParcel || isFetchingTrackEvents) {
    return (
      <div className={styles.filling}>
        <div className={styles.header}/>
        <div className={styles.body}>
          <Loader size="s"/>
        </div>
      </div>
    )
  }

  if (photo) {
    return (
      <div className={styles.filling}>
        <div className={styles.body}>
          <span
            className={styles.wrapper}
            onClick={() => setPhoto(undefined)}
          >
            <img
              className={cn(styles.picture,
                photoVisible && styles.pictureVisible
              )}
              alt="Photo"
              src={photo}
            />
          </span>
        </div>
      </div>
    )
  }

  if (!dataGetParcel?.item) return (<></>)

  return (
    <div className={styles.filling}>
      <div className={styles.header}>
        <div>
          <TrackingNumber
            externalId={dataGetParcel!.item.trackingNumber || ''}
            trackNumber={dataGetParcel!.item.directStreamExternalId || ''}
          />
        </div>
        {callFrom === 'awaitingTab' && (
          <div className={styles.actions}>
            <Tooltip
              action={
                <img
                  className={styles.icon}
                  alt="Info"
                  src={InfoIcon}
                />
              }
              text={
                <div className={styles.clue}>
                  <span><b>{t('cardParcelModalWindow.dispose')}</b></span>
                  <span>{t('cardParcelModalWindow.disposeText')}</span>
                  {!dataGetParcel!.item.reexportCancelReason && (
                    <>
                      <span><b>{t('cardParcelModalWindow.return')}</b></span>
                      <span>{t('cardParcelModalWindow.returnText')}</span>
                    </>
                  )}
                  {(parcel as TAwaitingParcel).deliverToLocalPrice && (
                    <>
                      <span><b>{t('cardParcelModalWindow.deliverToLocal')}</b></span>
                      <span>{t('cardParcelModalWindow.deliverToLocalText')}</span>
                    </>
                  )}
                </div>
              }
              position="bottom"
            />
            <div className={styles.buttons}>
              <Button
                onClick={() => {
                  handlerSetIsOpenParcelWindow(false);
                  handlerSetIsOpenDisposalProcessingWindow!(true);
                  handlerSetProcessingCalledFrom!('card');
                }}
                title={t('cardParcelModalWindow.dispose') + ((parcel as TAwaitingParcel).utilizationCompensation ? ` +${formatSumWithCurrency((parcel as TAwaitingParcel).utilizationCompensation!.amount, variableCurrency)}` : '')}
                type={ButtonType.RED_SKELETON}
              />
              {(parcel as TAwaitingParcel).deliverToLocalPrice && handlerSetIsOpenDeliverToLocalProcessingWindow && (
                <Button
                  onClick={() => {
                    handlerSetIsOpenParcelWindow(false);
                    handlerSetIsOpenDeliverToLocalProcessingWindow(true);
                    handlerSetProcessingCalledFrom!('card');
                  }}
                  title={t('cardParcelModalWindow.deliverToLocalFor') + ` ${formatSumWithCurrency((parcel as TAwaitingParcel).deliverToLocalPrice!.amount, variableCurrency)}`}
                  type={ButtonType.GREEN_SKELETON}
                />
              )}
              {(parcel as TAwaitingParcel).availableForResaleOrders?.length > 0 && handlerSetIsOpenResaleProcessingWindow && (
                <Button
                  onClick={() => {
                    handlerSetIsOpenParcelWindow(false);
                    handlerSetIsOpenResaleProcessingWindow(true);
                    handlerSetProcessingCalledFrom!('card');
                  }}
                  title={t('cardParcelModalWindow.resellFor') + ` ${formatSumWithCurrency((parcel as TAwaitingParcel).resalePrice.amount, variableCurrency)}`}
                  type={ButtonType.GREEN_SKELETON}
                />
              )}
              {!dataGetParcel!.item.reexportCancelReason && dataGetParcel!.item.reexportPrice && (
                <Button
                  onClick={() => {
                    handlerSetIsOpenParcelWindow(false);
                    handlerSetIsOpenReturnProcessingWindow!(true);
                    handlerSetProcessingCalledFrom!('card');
                  }}
                  title={t('cardParcelModalWindow.returnFor') + ` ${formatSumWithCurrency(dataGetParcel!.item.reexportPrice.amount, variableCurrency)}`}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <div className={styles.body}>
        {(dataGetParcel.item.trackingInfo || dataGetParcel.item.returnStreamTrackingEvents?.length) ? (
          <div>
            {dataGetParcel.item.returnStreamTrackingEvents?.length ? (
              <ToggleButtons
                value={statusView}
                onChange={setStatusView}
                items={[
                  ...(isUNI ? [{ id: "send", title: t("cardParcelModalWindow.statusesSend") }] : []),
                  { id: "return", title: t("cardParcelModalWindow.statusReturn") },
                ]}
              />
            ) : null}
            {dataGetParcel.item.trackingInfo ? (
              <div className={styles.tracking}>
                <span className={styles.trackingTitle}>
                  {t('parcelModalWindow.trackNumber')}
                </span>
                <div className={styles.trackingInfo}>
                  <span className={styles.trackingService}>
                    {dataGetParcel.item.trackingInfo.deliveryService}
                  </span>
                  <CopyTextToClipboard text={dataGetParcel.item.trackingInfo.trackNumber}>
                    <a
                      href={dataGetParcel.item.trackingInfo.url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <span className={styles.trackingNumber}>
                        {dataGetParcel.item.trackingInfo.trackNumber}
                      </span>
                    </a>
                  </CopyTextToClipboard>
                </div>
              </div>
            ) : null}
            {dataGetParcel.item.returnStreamTrackingEvents?.length ? (
              <ParcelModalTimeline
                items={statusView === "return"
                  ? dataGetParcel.item.returnStreamTrackingEvents.map(v => ({
                    ...v,
                    type: v.eventType,
                    moment: v.occurredAt,
                    message: v.eventName,
                  }))
                  : (trackEventsData?.trackEvents ?? [])
                }
                countryCode={dataGetParcel.item.countryCode}
                tracking={statusView === "return" ? (isUNI ? "clientReturn" : "clientReturnNotUNI") : "direct"}
              />
            ) : null}
          </div>
        ) : null}
        <div className={styles.info}>
          <div className={styles.col}>
            <Text
              color={TextColor.DARK_MUTED}
              ellipsis
              size={TextSize.XS}
            >
              {t('cardParcelModalWindow.article')}
            </Text>
            <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
              weight={TextWeight.BOLD}
            >
              {dataGetParcel!.item.vendorCode}
            </Text>
          </div>
          <div className={styles.col}>
            <Text
              color={TextColor.DARK_MUTED}
              size={TextSize.XS}
            >
              {t('cardParcelModalWindow.name')}
            </Text>
            <a
              href={dataGetParcel!.item.url}
              rel="noreferrer"
              target="_blank"
            >
              {dataGetParcel!.item.title}
            </a>
          </div>
          <div className={styles.col}>
            <Text
              color={TextColor.DARK_MUTED}
              size={TextSize.XS}
            >
              {t('cardParcelModalWindow.length')}
            </Text>
            <Text
              color={TextColor.PRIMARY}
              ellipsis
              size={TextSize.S}
            >
              {formatNumber(dataGetParcel!.item.dimensions.length.value)} {dataGetParcel!.item.dimensions.length.unit}
            </Text>
          </div>
          <div className={styles.col}>
            <Text
              color={TextColor.DARK_MUTED}
              size={TextSize.XS}
            >
              {t('cardParcelModalWindow.width')}
            </Text>
            <Text
              color={TextColor.PRIMARY}
              ellipsis
              size={TextSize.S}
            >
              {formatNumber(dataGetParcel!.item.dimensions.width.value)} {dataGetParcel!.item.dimensions.width.unit}
            </Text>
          </div>
          <div className={styles.col}>
            <Text
              color={TextColor.DARK_MUTED}
              size={TextSize.XS}
            >
              {t('cardParcelModalWindow.height')}
            </Text>
            <Text
              color={TextColor.PRIMARY}
              ellipsis
              size={TextSize.S}
            >
              {formatNumber(dataGetParcel!.item.dimensions.height.value)} {dataGetParcel!.item.dimensions.height.unit}
            </Text>
          </div>
          <div className={styles.col}>
            <Text
              color={TextColor.DARK_MUTED}
              size={TextSize.XS}
            >
              {t('cardParcelModalWindow.weight')}
            </Text>
            <Text
              color={TextColor.PRIMARY}
              ellipsis
              size={TextSize.S}
            >
              {formatNumber(dataGetParcel!.item.weight.value)} {dataGetParcel!.item.weight.unit}
            </Text>
          </div>
          <div className={styles.col}>
            <Text
              color={TextColor.DARK_MUTED}
              size={TextSize.XS}
            >
              {t('cardParcelModalWindow.price')}
            </Text>
            <Text
              color={TextColor.PRIMARY}
              ellipsis
              size={TextSize.S}
            >
              {formatSumWithCurrency(dataGetParcel!.item.priceCny, variableCurrency)}
            </Text>
          </div>
        </div>
        {callFrom !== 'processingTab' && (
          <div className={styles.block}>
            <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
              weight={TextWeight.BOLD}
            >
              {t('cardParcelModalWindow.conditionGoods')}
            </Text>
            <div className={styles.status}>
              <span className={styles.type}>
                <span
                  className={cn(styles.mark, dataGetParcel!.item.productConditionType === 'Defect' ? styles.red : styles.green)}
                />
                <Text
                  color={TextColor.PRIMARY}
                  size={TextSize.S}
                  weight={TextWeight.MEDIUM}
                >
                  {dataGetParcel!.item.productConditionType === 'Defect'
                    ? t(`cardParcelModalWindow.states.${dataGetParcel!.item.defectReasonType}`)
                    : t(`cardParcelModalWindow.states.${dataGetParcel!.item.productConditionType}`)
                  }
                </Text>
              </span>
              <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
              >
                {dataGetParcel!.item.returnReason}
              </Text>
            </div>
          </div>
        )}
        {isLoadingParcelPhoto ? (
          <Loader size="s"/>
        ) : (
          <>
            {returnPhotos.length > 0 && (
                <div className={styles.block}>
                  <Text
                    color={TextColor.PRIMARY}
                    size={TextSize.S}
                    weight={TextWeight.BOLD}
                  >
                    {t('cardParcelModalWindow.photoBuyer')}
                  </Text>
                  <div className={styles.photos}>
                    {returnPhotos.map((url: string, index: number) => (
                      <span
                        className={styles.photo}
                        key={index}
                        onClick={() => setPhoto(url)}
                      >
                        <img
                          className={styles.image}
                          alt="Photo"
                          src={url}
                        />
                      </span>
                    ))}
                  </div>
                </div>
            )}
            {callFrom !== 'processingTab' && checkingPhotos.length > 0 && (
              <div className={styles.block}>
                <Text
                  color={TextColor.PRIMARY}
                  size={TextSize.S}
                  weight={TextWeight.BOLD}
                >
                  {t('cardParcelModalWindow.photoVerification')}
                </Text>
                <div className={styles.photos}>
                  {checkingPhotos.map((url: string, index: number) => (
                    <span
                      className={styles.photo}
                      key={index}
                      onClick={() => setPhoto(url)}
                    >
                      <img
                        className={styles.image}
                        alt="Photo"
                        src={url}
                      />
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CardParcelModalWindow;
